// Modules
import React from 'react';
import {Route} from 'react-router-dom';

// Utils
import {MiddlewareComponent, RouteDefinition} from './types';
import {isParentRoute} from './utils';
import {
  adminOnlyMiddleware,
  createPermissionMiddleware,
  createMultiplePermissionsMiddleware,
} from './middlewares/middlewares';

/**
 * Envuelve un elemento con los middlewares especificados
 * Los middlewares se aplican en orden (el primero envuelve al resto)
 */
const wrapWithMiddlewares = (
  element: React.ReactNode,
  middlewares?: MiddlewareComponent[],
): React.ReactNode => {
  if (!middlewares?.length) {
    return element;
  }

  return middlewares.reduceRight(
    (children, Middleware) => <Middleware>{children}</Middleware>,
    element,
  );
};

/**
 * Genera una key única para cada ruta
 */
const generateRouteKey = (route: RouteDefinition, index: number): string => {
  if (route.index) return `index-${index}`;
  return `${route.path || 'route'}-${index}`;
};

/**
 * Obtiene los middlewares para una ruta, incluyendo los de permisos si están definidos.
 * Los middlewares de permisos se agregan después de los middlewares base de la ruta.
 */
const getRouteMiddlewares = (route: RouteDefinition): MiddlewareComponent[] => {
  const middlewares = [...(route.middlewares || [])];

  // Add permission middleware if requiredPermission is set
  if (route.requiredPermission) {
    // Check if it's an array of permissions or a single permission
    if (Array.isArray(route.requiredPermission)) {
      if (route.requiredPermission.length > 0) {
        middlewares.push(createMultiplePermissionsMiddleware(route.requiredPermission));
      }
    } else {
      middlewares.push(createPermissionMiddleware(route.requiredPermission));
    }
  }

  // Add adminOnly middleware if set
  if (route.adminOnly) {
    middlewares.push(adminOnlyMiddleware);
  }

  return middlewares;
};

/**
 * Renderiza un array de definiciones de rutas como componentes Route de React Router
 */
export const renderRoutes = (routes: RouteDefinition[]): React.ReactNode => {
  return routes
    .filter(route => route.enabled !== false)
    .map((route, index) => {
      const key = generateRouteKey(route, index);
      const middlewares = getRouteMiddlewares(route);
      const element = route.Component
        ? wrapWithMiddlewares(route.Component, middlewares)
        : undefined;

      if (route.index) {
        return <Route key={key} index element={element} />;
      }

      return (
        <Route key={key} path={route.path} element={element}>
          {isParentRoute(route) ? renderRoutes(route.children) : null}
        </Route>
      );
    });
};
